import React, { useEffect, useRef, useState } from "react";
import "./admin-payment.css";
import { FaSearch } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import { MdDelete } from "react-icons/md";
import { GrFormPrevious, GrFormNext } from "react-icons/gr";
import ToastNotification, {
  showToast,
} from "../../components/toasts/ToastNotification";
import {
  deleteTransactionForAdmin,
  getTransactionListForAdmin,
} from "./../../api/transaction.api";
import { paymentMethodText, statusText, statusType } from "../../utils";
import ConfirmModal from "../../components/modal/confirm-modal";
import { useUser } from "../../context/UserContext";
import { hasPermission } from "../../utils/auth";
import Pagination from "../../components/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

const ITEMS_PER_PAGE = 8;
export const AdminPayment = () => {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 500);

  const [selectedTransactionStatusType, setSelectedTransactionStatusType] =
    useState("all");
    const [selectedTransactionType, setSelectedTransactionType] =
    useState("all");
  const [transactions, setTransaction] = useState([]);

  const [openActionId, setOpenActionId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const { user } = useUser();

  const modalRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchTransactionList();
    document.title = "Admin - Giao dịch nạp tiền";
  }, [currentPage, selectedTransactionStatusType, debouncedSearchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  useEffect(() => {
    fetchTransactionList();
  }, [selectedTransactionStatusType, selectedTransactionType])

  const fetchTransactionList = async () => {
    try {
      setLoading(true);

      const res = await getTransactionListForAdmin(
        ITEMS_PER_PAGE,
        currentPage,
        debouncedSearchInput.trim(),
        selectedTransactionStatusType,
        selectedTransactionType
      );

      if (res.success) {
        let filtered = res.data.transactions;

        setTransaction(filtered);
        setTotalPages(res.data.totalPages);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setOpenActionId(null);
      }
    };

    if (openActionId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openActionId]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleDeleteTransaction = async (transactionId) => {
    setLoading(true);
    setIsModalOpen(false);
    try {
      const res = await deleteTransactionForAdmin(transactionId);

      if (res.success) {
        showToast("Xóa giao dịch thành công", "success");
        setSelectedIdToDelete("");

        fetchTransactionList();
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="#092339" />
      </div>
    );
  }

  return (
    <div className="admin-payment-container">
      <p className="tab-nav-title">Danh sách giao dịch</p>
      <div className="admin-product-header">
        <div className="search-filter-container">
          <div className="admin-search-input-container">
            <input
              type="text"
              placeholder="Tìm kiếm theo mã giao dịch"
              className="admin-search-input"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
            />
            <FaSearch className="admin-search-icon" />
          </div>
          <select
            className="filter-selection"
            value={selectedTransactionStatusType}
            onChange={(e) => {
              setSelectedTransactionStatusType(e.target.value);
            }}
          >
            <option value="" disabled>
              Trạng thái
            </option>
            <option value="all">Tất cả</option>
            <option value="success">Thành công</option>
            <option value="pending">Đang chờ</option>
            <option value="failed">Thất bại</option>
          </select>

          <select
            className="filter-selection"
            value={selectedTransactionType}
            onChange={(e) => {
              setSelectedTransactionType(e.target.value);
            }}
          >
            <option value="" disabled>
              Loại
            </option>
            <option value="all">Tất cả</option>
            <option value="bank_transfer">Ngân hàng</option>
            <option value="card">Thẻ cào</option>
          </select>
        </div>
        

      </div>
      <div className="product-table-container">
        <table className="product-table">
          <thead>
            <tr>
              <th>STT</th>
              <th>Mã giao dịch</th>
              <th>Tên người dùng</th>
              <th>Mệnh giá</th>
              <th>Phương thức</th>
              <th>Trạng thái</th>
              <th>Cổng thanh toán</th>
              <th>Thông tin</th>
              <th>Thời gian</th>
              {hasPermission(user?.role, "delete:payment") &&
                <th>Thao tác</th>}
            </tr>
          </thead>
          <tbody>
            {transactions.length > 0 ? (
              transactions.map((tx, index) => (
                <tr key={tx.transactionId}>
                  <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                  <td>{tx.transactionId}</td>
                  <td>
                    <a className="table-username" href={`/super-admin/users/view/${tx.userId?._id}`}>{tx.userId?.username}</a>
                  </td>
                  <td className="transaction-price">
                    {tx.amount.toLocaleString() || 0}
                  </td>
                  <td style={{ color: "#3498db" }}>
                    {paymentMethodText(tx.paymentMethod)}
                  </td>
                  <td>
                    <div
                      className={`transaction-status ${statusType(
                        tx.transactionStatus
                      )}`}
                    >
                      {statusText(tx.transactionStatus)}
                    </div>
                  </td>
                  <td>{tx.gateway === "empty_gateway" ? "NA" : tx.gateway}</td>
                  <td>{tx?.transaction_attributes ?
                    (
                      <>
                        <p>Serial: {tx?.transaction_attributes.serial}</p>
                        <p>Code: {tx?.transaction_attributes.code}</p></>
                    ) : ""}
                  </td>
                  <td>{new Date(tx.createdAt).toLocaleString()}</td>
                  {hasPermission(user?.role, "delete:payment") &&
                    <td className="action-cell">
                      <div className="action-buttons-container">
                        <button
                          onClick={() => {
                            setIsModalOpen(true);
                            setSelectedIdToDelete(tx.transactionId);
                          }}
                          className="delete-btn action-button"
                        >
                          <MdDelete className="action-icon" />
                        </button>
                      </div>
                    </td>}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10">Không có giao dịch</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={handlePrevPage}
          onNext={handleNextPage}
        />
      )}
      <ToastNotification />
      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={() => handleDeleteTransaction(selectedIdToDelete)}
        onClose={() => setIsModalOpen(false)}
        message={"Xác nhận bạn đang xóa một giao dịch"}
        title={"Xóa giao dịch"}
      />
    </div>
  );
};
